import React, { useState, useEffect } from "react";
import { Form, Input, Button, AutoComplete, Row, Col, Card, Space } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { doPost } from "../../../../../API/apis";
import { useParams } from "react-router-dom";
import Notification from "../../../../../components/Notification/Notification";
import showConfirmModal from "../../../../../components/ModalConfirm/ModalConfirm";
import { useSearchAddedLabs } from "../../../../../API/apiUtils";
import { useSearchDiagnosis } from "../../../../../API/apiUtils";

function ExternalLabs({ onUnsavedChange }) {
  const { id } = useParams();
  const [form] = Form.useForm();
  const [lab, setLab] = useState({
    lab_name: "",
    service_type: "",
    service_group: "",
  });
  const [edit, setEdit] = useState(false);
  const [labsList, setLabsList] = useState([]);
  const { diagnosisOptions, handleSearchDiagnosis } = useSearchDiagnosis(true);
  const [editingItemKey, setEditingItemKey] = useState(null);
  const { options, handleSearchLabs } = useSearchAddedLabs();

  const labInstructions = Form.useWatch("lab_instructions", form);
  const labDiagnosis = Form.useWatch("lab_diagnosis", form);

  const hasUnsavedData =
    labsList.length > 0 ||
    !!(lab?.lab_name && String(lab.lab_name).trim()) ||
    !!(labInstructions && String(labInstructions).trim()) ||
    !!(labDiagnosis && String(labDiagnosis).trim());

  useEffect(() => {
    onUnsavedChange?.(hasUnsavedData);
    return () => onUnsavedChange?.(false);
  }, [hasUnsavedData, onUnsavedChange]);

  useEffect(() => {
    if (!hasUnsavedData) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedData]);

  const onSelect = (value, option) => {
    const selectedLab = options.find((item) => item.value === value);
    if (selectedLab) {
      setLab({
        lab_name: selectedLab?.lab_name,
        service_type: selectedLab?.service_type,
        service_group: selectedLab?.service_group,
      });
    }
  };

  const handleExternalLabs = (values) => {
    // Use form values for lab_name if no lab is selected from dropdown
    const labName = lab?.lab_name || values.lab_name;
    const serviceType = lab?.service_type || "";
    const serviceGroup = lab?.service_group || "";
    
    const newLabs = {
      ...values,
      lab_name: labName,
      service_type: serviceType,
      service_group: serviceGroup,
      key: Date.now(),
    };
    setLabsList([...labsList, newLabs]);
    form.resetFields();
    setEdit(false);
    // Reset lab state after adding to list
    setLab({
      lab_name: "",
      service_type: "",
      service_group: "",
    });
  };

  const handleEdit = (item) => {
    form.setFieldsValue({
      lab_name: item.lab_name,
      lab_instructions: item.lab_instructions,
      lab_diagnosis: item.lab_diagnosis,
    });
    // Set lab state for editing
    setLab({
      lab_name: item.lab_name,
      service_type: item.service_type || "",
      service_group: item.service_group || "",
    });
    setEdit(true);
    setEditingItemKey(item.key);
  };

  const handleUpdate = (values) => {
    // For updates, check if lab state was recently updated (meaning user selected from dropdown)
    // If lab state has different values than the original item, use lab state
    // Otherwise, use form values (user typed free text)
    const originalItem = labsList.find(item => item.key === editingItemKey);
    const isNewSelection = lab?.lab_name && lab.lab_name !== originalItem?.lab_name;
    
    const labName = isNewSelection ? lab.lab_name : values.lab_name;
    const serviceType = isNewSelection ? lab.service_type : "";
    const serviceGroup = isNewSelection ? lab.service_group : "";
    
    const updatedValues = {
      ...values,
      lab_name: labName,
      service_type: serviceType,
      service_group: serviceGroup,
    };
    
    const updatedLabsList = labsList.map((item) =>
      item.key === editingItemKey ? { ...item, ...updatedValues } : item
    );

    setLabsList(updatedLabsList);
    form.resetFields();
    setEdit(false);
    setEditingItemKey(null); // Reset the editing item key
    // Reset lab state after updating
    setLab({
      lab_name: "",
      service_type: "",
      service_group: "",
    });
  };

  const handleDelete = (item) => {
    showConfirmModal({
      title: "Confirm Delete?",
      icon: null,
      content: "Are you sure you want to delete this?",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      className: "custom-modal",
      onOk() {
        const updatedLabsList = labsList.filter((lab) => lab.key !== item.key);
        setLabsList(updatedLabsList);
      },
    });
  };

  const handleFormClear = () => {
    setEdit(false);
    form.resetFields();
    // Reset lab state when clearing form
    setLab({
      lab_name: "",
      service_type: "",
      service_group: "",
    });
  };

  const handleClear = () => {
    showConfirmModal({
      title: "Are you sure you want to delete this item?",
      icon: <DeleteOutlined />,
      content: "This action cannot be undone.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk() {
        setLabsList([]);
        setEdit(false);
      },
    });
  };

  const handleExternalLabOrders = async () => {
    const data = {
      lab_name: labsList,
    };
    try {
      const response = await doPost(
        `/labs/images/create/${id}/?q=external`,
        data
      );
      if (response.status === 201) {
        Notification.success("External Lab Orders Created successfully");
        setLabsList([]);
      }
    } catch (error) {
      Notification.error("Something went Wrong");
    }
  };

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} md={24} lg={24} xl={12}>
        <Card className="shadow-class" title="External Labs">
          <Form
            layout="vertical"
            name="external-lab"
            form={form}
            onFinish={edit ? handleUpdate : handleExternalLabs}
          >
            <Form.Item
              name="lab_name"
              tooltip="Name, Group, Type, Price"
              label="Search External Labs"
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: "Enter Lab name",
                },
              ]}
            >
              <AutoComplete
                options={options}
                allowClear={true}
                onSelect={onSelect}
                onSearch={handleSearchLabs}
                placeholder="Search Inhouse Labs Only"
                maxLength={100}
              />
            </Form.Item>
            <Form.Item name="lab_instructions" label="Lab Instructions">
              <Input.TextArea
                placeholder="Enter Instructions here"
                rows={4}
                maxLength={500}
                showCount
              />
            </Form.Item>
            <Form.Item name="lab_diagnosis" label="Diagnosis">
              <AutoComplete
                onSearch={handleSearchDiagnosis}
                options={diagnosisOptions}
                placeholder="Search Associated Diagnosis"
                maxLength={300}
              />
            </Form.Item>
            <div className="flex justify-end">
              <Button onClick={handleFormClear}>Clear</Button>
              <Button className="ml-2" type="primary" htmlType="submit">
                {edit ? "Update Lab" : "Add to List"}
              </Button>
            </div>
          </Form>
        </Card>
      </Col>
      <Col xs={24} md={24} lg={24} xl={12}>
        <Card
          className="shadow-class"
          title="External Labs Pad"
          extra={
            <Space>
              {labsList.length !== 0 && (
                <Button type="link" onClick={handleClear}>
                  Clear All
                </Button>
              )}
            </Space>
          }
        >
          <div className="mb-2">
            <ul>
              {labsList.map((item) => (
                <li key={item.key} className="flex justify-between">
                  <span>{item.lab_name}</span>
                  <div>
                    <EditOutlined
                      className="text-blue-400"
                      onClick={() => {
                        handleEdit(item);
                      }}
                    />
                    <DeleteOutlined
                      className="ml-2 mr-4 text-red-600"
                      onClick={() => {
                        handleDelete(item);
                      }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex justify-end mb-4">
            <Button
              type={labsList.length === 0 ? "default" : "primary"}
              disabled={labsList.length === 0}
              onClick={handleExternalLabOrders}
            >
              Order
            </Button>
          </div>
          <p className="text-gray-400 text-center">
            Invoice will NOT be created for External Lab Orders.
          </p>
        </Card>
      </Col>
    </Row>
  );
}

export default ExternalLabs;
